import { useEffect, useMemo, useState } from "react"
import { Navigate, useNavigate, useSearchParams } from "react-router"

import { ApiError } from "@/api/client"
import {
  useConnectSelectedTikTokAccounts,
  useSelectTikTokAccounts,
  type SelectableAccount,
} from "@/api/hooks/tiktokAdmin"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"

/**
 * React port of `templates/select_tiktok_accounts.html` (T3.12).
 *
 * This page sits OUTSIDE `<AppShell/>` and outside `<ProtectedRoute/>`
 * — see `router.tsx` for the rationale. A browser arriving here from
 * the TikTok OAuth callback may not have a JWT cookie; authentication
 * falls back to the `?session=<token>` query param, which the backend
 * GET endpoint resolves via `resolve_select_session_user`.
 *
 * Parity reference: `docs/migration/parity/select_tiktok_accounts.md`.
 */

function Section({
  title,
  color,
  icon,
  accounts,
  checked,
  onToggle,
  statusLabel,
  statusClass,
}: {
  title: string
  color: string
  icon: string
  accounts: SelectableAccount[]
  checked: Set<string>
  onToggle: (id: string, value: boolean) => void
  statusLabel: string
  statusClass: string
}) {
  if (accounts.length === 0) return null
  return (
    <div className="mb-6 border-b-2 border-[#4a4a4a] pb-4 last:mb-0 last:border-b-0 last:pb-0">
      <div
        className="mb-3 flex items-center gap-2 text-sm font-semibold"
        style={{ color }}
      >
        <span>{icon}</span>
        <span>
          {title} ({accounts.length})
        </span>
      </div>
      {accounts.map((account) => {
        const id = `account_${account.advertiser_id}`
        return (
          <label
            key={account.advertiser_id}
            htmlFor={id}
            className="mb-2 flex cursor-pointer items-center gap-4 rounded-md border border-transparent p-4 transition-colors hover:border-[#4a4a4a] hover:bg-[#353535]"
          >
            <Checkbox
              id={id}
              checked={checked.has(account.advertiser_id)}
              onCheckedChange={(value) =>
                onToggle(account.advertiser_id, value === true)
              }
              className="h-5 w-5"
            />
            <div className="flex-1">
              <div className="mb-1 font-semibold text-white">
                {account.advertiser_name}
              </div>
              <div className="font-mono text-sm text-white">
                ID: {account.advertiser_id}
              </div>
            </div>
            <span
              className={`rounded-full px-3 py-1 text-xs font-semibold ${statusClass}`}
            >
              {statusLabel}
            </span>
          </label>
        )
      })}
    </div>
  )
}

export default function SelectTikTokAccountsPage() {
  const [searchParams] = useSearchParams()
  const session = searchParams.get("session")
  const navigate = useNavigate()

  const query = useSelectTikTokAccounts(session)
  const connectMutation = useConnectSelectedTikTokAccounts()

  const data = query.data
  const accounts = useMemo(() => data?.accounts ?? [], [data])
  const newAccounts = useMemo(
    () => accounts.filter((a) => !a.already_connected),
    [accounts],
  )
  const existingAccounts = useMemo(
    () => accounts.filter((a) => a.already_connected),
    [accounts],
  )

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [seededFor, setSeededFor] = useState<unknown>(null)
  const [inlineError, setInlineError] = useState<string | null>(null)

  // Seed default-selected set from the first successful fetch: new
  // accounts checked, existing accounts unchecked (parity lines 278 and
  // 300). This is the "adjust state when a prop changes" pattern from
  // https://react.dev/learn/you-might-not-need-an-effect — calling
  // `setState` during render is cheap when guarded and avoids the
  // cascading-render warning that `useEffect` would trigger.
  if (data && seededFor !== data) {
    setSeededFor(data)
    setSelectedIds(
      new Set(
        data.accounts
          .filter((a) => !a.already_connected)
          .map((a) => a.advertiser_id),
      ),
    )
  }

  // Auto-hide inline error after 5 seconds, matching the legacy
  // `showError()` setTimeout in the Jinja template (lines 355-362).
  useEffect(() => {
    if (!inlineError) return
    const timer = window.setTimeout(() => setInlineError(null), 5000)
    return () => window.clearTimeout(timer)
  }, [inlineError])

  const allSelected = useMemo(() => {
    if (accounts.length === 0) return false
    return accounts.every((a) => selectedIds.has(a.advertiser_id))
  }, [accounts, selectedIds])

  if (!session) {
    return <Navigate to="/admin?error=invalid_session" replace />
  }

  if (query.isLoading) {
    return (
      <div className="min-h-screen bg-[#2a2a2a] p-8 text-white">
        <div className="mx-auto max-w-[800px] rounded-xl border border-[#4a4a4a] bg-[#3a3a3a] p-8 text-center text-white shadow-lg">
          Loading...
        </div>
      </div>
    )
  }

  if (query.isError) {
    const err = query.error
    if (err instanceof ApiError) {
      if (err.status === 401) {
        return <Navigate to="/admin?error=not_authenticated" replace />
      }
      if (err.status === 400) {
        return <Navigate to="/admin?error=session_expired" replace />
      }
    }
    const message = err instanceof Error ? err.message : String(err)
    return (
      <div className="min-h-screen bg-[#2a2a2a] p-8 text-white">
        <div className="mx-auto max-w-[800px] rounded-xl border border-[#4a4a4a] bg-[#3a3a3a] p-8 shadow-lg">
          <Alert variant="destructive">
            <AlertDescription>Error loading accounts: {message}</AlertDescription>
          </Alert>
        </div>
      </div>
    )
  }

  const toggleOne = (id: string, value: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (value) next.add(id)
      else next.delete(id)
      return next
    })
  }

  const toggleAll = (value: boolean) => {
    if (value) {
      setSelectedIds(new Set(accounts.map((a) => a.advertiser_id)))
    } else {
      setSelectedIds(new Set())
    }
  }

  const handleConnect = async () => {
    if (selectedIds.size === 0) {
      setInlineError("Please select at least one account to connect.")
      return
    }
    setInlineError(null)
    try {
      const data = await connectMutation.mutateAsync({
        session_token: session,
        selected_account_ids: Array.from(selectedIds),
      })
      if (data.success === true) {
        navigate("/admin?tiktok_connected=true", { replace: true })
      } else {
        setInlineError(data.message ?? "Failed to connect accounts")
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setInlineError(err.message)
      } else if (err instanceof Error) {
        setInlineError(`Error connecting accounts: ${err.message}`)
      } else {
        setInlineError("Error connecting accounts")
      }
    }
  }

  const handleCancel = () => {
    const confirmed = window.confirm(
      "Are you sure you want to cancel? The OAuth session will expire and you will need to reconnect.",
    )
    if (confirmed) {
      navigate("/admin", { replace: true })
    }
  }

  const isEmpty = accounts.length === 0
  const connectDisabled = selectedIds.size === 0 || connectMutation.isPending

  return (
    <div className="min-h-screen bg-[#2a2a2a] p-8 text-white">
      <div className="mx-auto max-w-[800px] rounded-xl border border-[#4a4a4a] bg-[#3a3a3a] p-8 shadow-lg">
        <h1 className="mb-4 text-[1.75rem] font-semibold text-white">
          Select TikTok Accounts to Connect
        </h1>
        <p className="mb-8 leading-relaxed text-white">
          Select which TikTok advertiser accounts you want to connect to the
          platform. Accounts that are already connected will be updated with
          the new access token.
        </p>

        {inlineError ? (
          <Alert
            variant="destructive"
            className="mb-6 border-[#7f1d1d] bg-[#4a2a2a] text-[#f87171]"
          >
            <AlertDescription>{inlineError}</AlertDescription>
          </Alert>
        ) : null}

        <div className="mb-8 max-h-[500px] overflow-y-auto rounded-lg border border-[#4a4a4a] bg-[#2d2d2d] p-6">
          {isEmpty ? (
            <div className="p-4 text-center text-white">
              No advertiser accounts found on this TikTok user.
            </div>
          ) : (
            <>
              <label
                htmlFor="selectAll"
                className="mb-4 flex cursor-pointer items-center gap-3 rounded-md bg-[#353535] p-4 transition-colors hover:bg-[#404040]"
              >
                <Checkbox
                  id="selectAll"
                  checked={allSelected}
                  onCheckedChange={(value) => toggleAll(value === true)}
                  className="h-5 w-5"
                />
                <span className="flex-1 font-semibold text-white">
                  Select All
                </span>
              </label>

              <Section
                title="New Accounts"
                color="#4ade80"
                icon="✨"
                accounts={newAccounts}
                checked={selectedIds}
                onToggle={toggleOne}
                statusLabel="New"
                statusClass="bg-[#1e4d3e] text-[#4ade80]"
              />
              <Section
                title="Already Connected"
                color="#9ca3af"
                icon="✓"
                accounts={existingAccounts}
                checked={selectedIds}
                onToggle={toggleOne}
                statusLabel="Already Connected"
                statusClass="border border-[#555555] bg-[#3a3a3a] text-white"
              />
            </>
          )}
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="secondary"
            onClick={handleCancel}
            className="border border-[#7a808b]/45 bg-[#636b78] text-white shadow-sm hover:bg-[#58606c]"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConnect}
            disabled={connectDisabled}
            className="w-full border border-[#8fa0ef]/35 bg-[#7488e8] text-white shadow-sm hover:bg-[#687bda] disabled:bg-[#4a4a4a] disabled:text-white sm:w-auto"
          >
            {connectMutation.isPending
              ? "Connecting..."
              : "Connect Selected Accounts"}
          </Button>
        </div>
      </div>
    </div>
  )
}
